import type { OnboardingStep, OnboardingTour } from '../types';
import { ensureRestaurantSelected } from '../tourRestaurant';
import {
  getCostPriceLossValues,
  getCostPriceStandoutValues,
  getPracticeDishValues,
  hasOpenableDish,
} from '../costPriceValues';
import { anchorExists, resolvePracticeStage } from '../practice/costPracticeResolver';
import { practiceDish, PRACTICE_SUBRECIPE_ID } from '../practice/practiceGuards';
import { allOf, inputSettled, watchChange } from '../advanceHelpers';

const skip = 'onboarding.skip';
const next = 'onboarding.next';
const route = '/costprice';

/**
 * The overview. `cost-welcome` puts a single restaurant in the selector because the whole
 * page early-returns a "pick a restaurant" notice under All Restaurants — the old
 * `cost-restaurant` step that asked the user to do it by hand is gone, since the only
 * case it could still cover is an account with no restaurants at all, where the selector
 * has nothing to offer either.
 */
const overviewSteps: OnboardingStep[] = [
  {
    id: 'cost-welcome', route, target: 'cost-page', layout: 'compact', illustration: 'welcomeCost',
    titleKey: 'onboarding.costPrice.welcome.title', descriptionKey: 'onboarding.costPrice.welcome.description',
    primaryLabelKey: 'onboarding.costPrice.welcome.cta', secondaryLabelKey: skip,
    onEnter: ensureRestaurantSelected,
  },
  ...(['total', 'avg'] as const).map(kpi => ({
    id: `cost-kpi-${kpi}`, route, target: `cost-kpi-${kpi}`, layout: 'compact' as const,
    titleKey: `onboarding.costPrice.kpis.${kpi}.title`, descriptionKey: `onboarding.costPrice.kpis.${kpi}.description`,
    primaryLabelKey: next, showArrow: true,
  })),
  {
    id: 'cost-chooser', route, layout: 'chooser',
    titleKey: 'onboarding.costPrice.chooser.title', descriptionKey: 'onboarding.costPrice.chooser.description',
    primaryLabelKey: 'onboarding.costPrice.chooser.completeCta', secondaryLabelKey: skip,
    onEnter: ensureRestaurantSelected,
    chooserTasks: [
      { illustration: 'costIngredients', titleKey: 'onboarding.costPrice.chooser.ingredient.title', descriptionKey: 'onboarding.costPrice.chooser.ingredient.description', ctaKey: 'onboarding.start', branch: 'addIngredient' },
      { illustration: 'costSoup', titleKey: 'onboarding.costPrice.chooser.subrecipe.title', descriptionKey: 'onboarding.costPrice.chooser.subrecipe.description', ctaKey: 'onboarding.start', branch: 'createSubrecipe' },
    ],
  },
  {
    id: 'cost-standout', route, layout: 'hero', illustration: 'costStandout',
    target: 'cost-kpis', arrowTarget: 'cost-kpi-popular',
    titleKey: 'onboarding.costPrice.standout.title', descriptionKey: 'onboarding.costPrice.standout.description',
    values: getCostPriceStandoutValues,
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, padding: 4,
  },
  {
    id: 'cost-loss', route, layout: 'compact', illustration: 'costAb', target: 'cost-col-margin',
    titleKey: 'onboarding.costPrice.loss.title', descriptionKey: 'onboarding.costPrice.loss.description',
    values: getCostPriceLossValues,
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, padding: 6, radius: 12,
  },
  {
    id: 'cost-table', route, layout: 'compact', illustration: 'costCalculator', target: 'cost-table',
    titleKey: 'onboarding.costPrice.table.title', descriptionKey: 'onboarding.costPrice.table.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'above',
  },
  {
    // Keep this overview spotlight read-only. Advancing resets the overview fixture for
    // the hands-on exercise, so opening its dish at the same time races that reset. The
    // next step owns the first interactive row.
    id: 'cost-open-dish', route, layout: 'compact', illustration: 'click',
    target: 'cost-dish-first',
    titleKey: 'onboarding.costPrice.openDish.title', descriptionKey: 'onboarding.costPrice.openDish.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'above',
    isAvailable: hasOpenableDish,
    padding: 8, radius: 12,
  },

];

/** Mini-tour launched from the chooser "Add an ingredient" Start. */
const ingredientSteps: OnboardingStep[] = [
  {
    // The hole IS the practice row. A table-wide hole with allowTargetInteraction would
    // let a click land on a placeholder row, which creates a real analysis.
    id: 'cost-ing-zero', route, target: 'cost-dish-zero',
    layout: 'compact', illustration: 'costCreateDish',
    titleKey: 'onboarding.costPrice.addIngredient.zero.title',
    descriptionKey: 'onboarding.costPrice.addIngredient.zero.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'above',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-ing-add', route, target: 'cost-add-ingredient', layout: 'compact', illustration: 'costAddHere',
    titleKey: 'onboarding.costPrice.addIngredient.add.title',
    descriptionKey: 'onboarding.costPrice.addIngredient.add.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-ing-orders', route, target: 'cost-add-from-orders', layout: 'compact', illustration: 'costOrdered',
    titleKey: 'onboarding.costPrice.addIngredient.orders.title',
    descriptionKey: 'onboarding.costPrice.addIngredient.orders.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left', padding: 8, radius: 12,
  },
  {
    id: 'cost-ing-search', route, target: 'cost-add-search', layout: 'compact', illustration: 'costFind',
    titleKey: 'onboarding.costPrice.addIngredient.search.title',
    descriptionKey: 'onboarding.costPrice.addIngredient.search.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    ...inputSettled('cost-add-search'), showArrow: true, placement: 'left',
    allowTargetInteraction: true, padding: 6, radius: 10,
  },
  {
    id: 'cost-ing-pick', route, target: 'cost-add-lamb', layout: 'compact', illustration: 'click',
    titleKey: 'onboarding.costPrice.addIngredient.pick.title',
    descriptionKey: 'onboarding.costPrice.addIngredient.pick.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    // The picker prefills its own default here, and the asked-for 200 differs from it,
    // so an edit is a real signal and this step waits for one instead of offering Next.
    id: 'cost-ing-qty', route, target: 'cost-add-lamb', arrowTarget: 'cost-add-qty',
    layout: 'compact', illustration: 'costQty',
    titleKey: 'onboarding.costPrice.addIngredient.qty.title',
    descriptionKey: 'onboarding.costPrice.addIngredient.qty.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    ...inputSettled('cost-add-qty'), showArrow: true, placement: 'left',
    allowTargetInteraction: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-ing-confirm', route, target: 'cost-add-confirm', layout: 'compact', illustration: 'costCart',
    titleKey: 'onboarding.costPrice.addIngredient.confirm.title',
    descriptionKey: 'onboarding.costPrice.addIngredient.confirm.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-ing-added', route, target: 'cost-dish-ingredient', layout: 'compact', illustration: 'costCheck',
    titleKey: 'onboarding.costPrice.addIngredient.added.title',
    descriptionKey: 'onboarding.costPrice.addIngredient.added.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left', padding: 8, radius: 12,
  },
];

/** Mini-tour launched from the chooser "Create a sub-recipe" Start. */
const subrecipeSteps: OnboardingStep[] = [
  {
    id: 'cost-sub-house', route, target: 'cost-add-ingredient', layout: 'spotlight', illustration: 'costMadeInHouse',
    titleKey: 'onboarding.costPrice.createSubrecipe.house.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.house.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-tab', route, target: 'cost-add-subrecipes-tab', layout: 'compact', illustration: 'click',
    titleKey: 'onboarding.costPrice.createSubrecipe.tab.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.tab.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-reuse', route, target: 'cost-add-subrecipe-list', layout: 'compact', illustration: 'recipeBook',
    titleKey: 'onboarding.costPrice.createSubrecipe.reuse.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.reuse.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left', padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-create', route, target: 'cost-add-create-subrecipe', layout: 'compact', illustration: 'normalBook',
    titleKey: 'onboarding.costPrice.createSubrecipe.create.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.create.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    // The name field starts empty, so "has a value" really is the user having typed one.
    id: 'cost-sub-name', route, target: 'cost-subrecipe-name', layout: 'compact', illustration: 'pencil',
    titleKey: 'onboarding.costPrice.createSubrecipe.name.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.name.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, ...inputSettled('cost-subrecipe-name', { minLength: 2 }),
    padding: 6, radius: 10,
  },
  {
    // The editor starts this at 1 and the copy asks for 500, so the edit is the signal.
    id: 'cost-sub-portions', route, target: 'cost-subrecipe-portions', layout: 'compact', illustration: 'pencil',
    titleKey: 'onboarding.costPrice.createSubrecipe.portions.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.portions.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    ...inputSettled('cost-subrecipe-portions'), showArrow: true, placement: 'left',
    allowTargetInteraction: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-add-ings', route, target: 'cost-subrecipe-add-ingredient', layout: 'compact', illustration: 'basket',
    titleKey: 'onboarding.costPrice.createSubrecipe.addIngredients.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.addIngredients.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-browse', route, target: 'cost-sub-add-from-orders', layout: 'compact', illustration: 'basket',
    titleKey: 'onboarding.costPrice.createSubrecipe.browse.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.browse.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left', padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-butter', route, target: 'cost-sub-add-butter', arrowTarget: 'cost-sub-add-qty',
    layout: 'compact', illustration: 'butter',
    titleKey: 'onboarding.costPrice.createSubrecipe.butter.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.butter.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    // Pick the product, then settle the quantity. The picker prefills 100 for a gram
    // product and this step asks for 100, so "changed" would never become true — the
    // selection is the act, and the quantity only has to come to rest.
    ...allOf(
      watchChange(() => anchorExists('cost-sub-add-qty')),
      inputSettled('cost-sub-add-qty', { changedFromInitial: false }),
    ), showArrow: true, placement: 'left',
    allowTargetInteraction: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-confirm-butter', route, target: 'cost-sub-add-confirm', layout: 'compact', illustration: 'basket',
    titleKey: 'onboarding.costPrice.createSubrecipe.confirmButter.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.confirmButter.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-one-more', route, target: 'cost-subrecipe-add-ingredient', layout: 'compact', illustration: 'basket',
    titleKey: 'onboarding.costPrice.createSubrecipe.oneMore.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.oneMore.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-find-garlic', route, target: 'cost-sub-add-search', layout: 'compact', illustration: 'costFind',
    titleKey: 'onboarding.costPrice.createSubrecipe.findGarlic.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.findGarlic.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    ...inputSettled('cost-sub-add-search'), showArrow: true, placement: 'left',
    allowTargetInteraction: true, padding: 6, radius: 10,
  },
  {
    id: 'cost-sub-garlic', route, target: 'cost-sub-add-garlic', arrowTarget: 'cost-sub-add-qty',
    layout: 'compact', illustration: 'basket',
    titleKey: 'onboarding.costPrice.createSubrecipe.garlic.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.garlic.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    // Here the asked-for 50 g does differ from the prefilled 100, so the edit counts.
    ...allOf(
      watchChange(() => anchorExists('cost-sub-add-qty')),
      inputSettled('cost-sub-add-qty'),
    ), showArrow: true, placement: 'left',
    allowTargetInteraction: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-confirm-garlic', route, target: 'cost-sub-add-confirm', layout: 'compact', illustration: 'basket',
    titleKey: 'onboarding.costPrice.createSubrecipe.confirmGarlic.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.confirmGarlic.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-save', route, target: 'cost-subrecipe-save', layout: 'compact', illustration: 'check',
    titleKey: 'onboarding.costPrice.createSubrecipe.save.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.save.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-sauce-qty', route, target: 'cost-add-garlic-butter', arrowTarget: 'cost-add-subrecipe-qty',
    layout: 'compact', illustration: 'butter',
    titleKey: 'onboarding.costPrice.createSubrecipe.sauceQty.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.sauceQty.description',
    primaryLabelKey: next, secondaryLabelKey: skip,
    // Same prefill trap as the butter step: the box arrives at the 100 the copy asks for,
    // so picking the sauce is the act that advances, not a value that never changes.
    ...allOf(
      watchChange(() => anchorExists('cost-add-subrecipe-qty')),
      inputSettled('cost-add-subrecipe-qty', { changedFromInitial: false }),
    ), showArrow: true, placement: 'left',
    allowTargetInteraction: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-add-sauce', route, target: 'cost-add-confirm', layout: 'compact', illustration: 'basket',
    titleKey: 'onboarding.costPrice.createSubrecipe.addSauce.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.addSauce.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceOnTargetClick: true, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-recipe-complete', route, target: 'cost-dish-subrecipe', layout: 'spotlight', illustration: 'completeCost',
    titleKey: 'onboarding.costPrice.createSubrecipe.recipeComplete.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.recipeComplete.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left', padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-cost-together', route, target: 'cost-detail-totalcost', layout: 'compact', illustration: 'recipeBook2',
    titleKey: 'onboarding.costPrice.createSubrecipe.costTogether.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.costTogether.description',
    values: getPracticeDishValues,
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left', padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-performs', route, target: 'cost-table', emphasisTarget: 'cost-dish-walkthrough',
    layout: 'compact', illustration: 'costChart',
    titleKey: 'onboarding.costPrice.createSubrecipe.performs.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.performs.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'above', padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-rename', route, target: 'cost-detail-rename', layout: 'compact', illustration: 'pencil',
    titleKey: 'onboarding.costPrice.createSubrecipe.rename.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.rename.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, padding: 8, radius: 12,
  },
  {
    // The copy describes removing one component of the dish, so it points at a row's own
    // bin rather than the header control that deletes the whole dish. This branch adds
    // only the sauce, so the sauce row's bin is the one on screen.
    id: 'cost-sub-cleanup', route, target: 'cost-subrecipe-delete', layout: 'compact', illustration: 'bin',
    titleKey: 'onboarding.costPrice.createSubrecipe.cleanup.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.cleanup.description',
    primaryLabelKey: next, secondaryLabelKey: skip, showArrow: true, placement: 'left',
    allowTargetInteraction: true, advanceWhen: () => {
      const dish = practiceDish();
      return !!dish && !dish.ingredients.some(row => row.subrecipeId === PRACTICE_SUBRECIPE_ID);
    }, padding: 8, radius: 12,
  },
  {
    id: 'cost-sub-complete', route, layout: 'complete', illustration: 'completeCost',
    titleKey: 'onboarding.costPrice.createSubrecipe.complete.title',
    descriptionKey: 'onboarding.costPrice.createSubrecipe.complete.description',
    primaryLabelKey: 'onboarding.goToCostPrice', secondaryLabelKey: 'onboarding.restart',
    secondaryAction: 'restart', primaryRoute: route,
  },
];

const ingredientComplete: OnboardingStep = {
  id: 'cost-ing-complete', route, layout: 'complete', illustration: 'completeCost',
  titleKey: 'onboarding.costPrice.practice.complete.title',
  descriptionKey: 'onboarding.costPrice.practice.complete.description',
  primaryLabelKey: 'onboarding.goToCostPrice', secondaryLabelKey: 'onboarding.restart', secondaryAction: 'restart',
};

/**
 * Mark a branch as practice and give each step its preparation. `onEnter` never performs
 * the step's own action — it only brings the real editor to the state the copy assumes,
 * so a user who clicks and a user who presses Next both land on the same screen.
 */
const asPractice = (kind: 'ingredient' | 'subrecipe', steps: OnboardingStep[]): OnboardingStep[] =>
  steps.map(step => {
    // A step spread with one of the advance helpers already owns an `onEnter` that takes
    // the field's baseline. Run the stage preparation first, then hand back whatever
    // cleanup the helper wants, so both survive.
    const stepOnEnter = step.onEnter;
    return {
      ...step,
      practice: kind,
      practiceStage: step.id,
      onEnter: () => {
        resolvePracticeStage(step.id);
        return stepOnEnter?.();
      },
    };
  });

export const costPriceAddIngredientSteps = asPractice('ingredient', [...ingredientSteps, ingredientComplete]);
export const costPriceCreateSubrecipeSteps = asPractice('subrecipe', subrecipeSteps);

export const costPriceSteps: OnboardingStep[] = [
  ...overviewSteps.map(step => ({ ...step, practice: 'overview' as const })),
  ...costPriceAddIngredientSteps.filter(step => step.layout !== 'complete'),
  ...costPriceCreateSubrecipeSteps.map(step => step.id === 'cost-sub-complete' ? {
    ...step,
    titleKey: 'onboarding.costPrice.fullComplete.title',
    descriptionKey: 'onboarding.costPrice.fullComplete.description',
    primaryLabelKey: 'onboarding.viewUtilization',
    primaryRoute: '/utilization',
    primaryTour: 'utilization',
    secondaryLabelKey: 'onboarding.explorePage',
    secondaryAction: 'complete' as const,
  } : step),
];

export const costPriceTour: OnboardingTour = {
  id: 'costPrice', paths: [route], steps: costPriceSteps,
  branches: { addIngredient: costPriceAddIngredientSteps, createSubrecipe: costPriceCreateSubrecipeSteps },
};
